import { useState, useEffect } from 'react';
import { DocumentIcon, DocumentTextIcon, FolderIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { Document, Page, pdfjs } from 'react-pdf';
// Set the PDF.js worker source to use a local worker file
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

interface FilePreviewProps {
  file: File;
  onRemove: () => void;
}

export default function FilePreview({ file, onRemove }: FilePreviewProps) {
  const { theme } = useTheme();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  // Check file types
  const isImage = file.type.startsWith('image/');
  const isPdf = file.type === 'application/pdf';
  const isZip = file.type === 'application/zip';
  
  // For PDF rendering
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  
  useEffect(() => {
    // Create preview URL for images and PDFs
    if (isImage || isPdf) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      
      // Clean up the object URL when component unmounts
      return () => URL.revokeObjectURL(objectUrl);
    }
    return undefined;
  }, [file, isImage, isPdf]);
  
  // Handle PDF document loading
  const onDocumentLoadSuccess = () => {
    setPdfLoading(false);
  };
  
  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setPdfError(true);
    setPdfLoading(false);
  };
  

  
  return (
    <div className={`relative group border rounded-lg p-2 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
      {isImage && previewUrl ? (
        <div className={`aspect-square w-full overflow-hidden rounded-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <img
            src={previewUrl}
            alt={file.name}
            className="h-full w-full object-contain"
          />
        </div>
      ) : isPdf && previewUrl ? (
        <div className={`aspect-square w-full overflow-hidden rounded-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
          {pdfError ? (
            <div className="h-full w-full flex flex-col items-center justify-center text-red-500">
              <DocumentTextIcon className="h-12 w-12 mb-2" />
              <span className="text-xs">Error loading PDF</span>
            </div>
          ) : (
            <div className="h-full w-full flex items-center justify-center overflow-hidden">
              <Document
                file={previewUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                }
              >
                {!pdfLoading && (
                  <Page 
                    pageNumber={1} 
                    width={150} 
                    height={150}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    scale={2.0}
                  />
                )}
              </Document>
            </div>
          )}
        </div>
      ) : isZip  ? (
        <div className={`aspect-square w-full flex items-center justify-center rounded-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <FolderIcon className="h-12 w-12 text-gray-400" />
        </div>
      ) : (
        <div className={`aspect-square w-full flex items-center justify-center rounded-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <DocumentIcon className="h-12 w-12 text-gray-400" />
        </div>
      )}
      
      <div className={`mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
        <p className="text-sm truncate" title={file.name}>
          {file.name}
        </p>
        <p className="text-xs text-gray-500">
          {(file.size / 1024).toFixed(1)} KB
        </p>
      </div>
      
      {/* Remove button */}
      <button
        type="button"
        onClick={onRemove}
        className={`absolute top-2 right-2 p-1 rounded-md ${theme === 'dark' ? 'bg-gray-800/90 hover:bg-gray-700 text-red-400 hover:text-red-300' : 'bg-white/90 hover:bg-gray-100 text-red-500 hover:text-red-700'}`}
        title="Remove"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
